// Ops Desk — the Ops team's own dashboard, also viewable by the dispatch
// admin: their queue by age and reason, their tracking chase-work, and what
// the team got done this week. Same measures as the owner cockpit, surfaced
// to the people doing the work first. Every number opens its order list
// (shared drill modal) and exports CSV.

import { useEffect, useState } from 'react';
import { opsDeskApi } from '../api/services';
import InfoTip from '../components/InfoTip';
import DrillModal, { useDrill } from '../components/DrillModal';
import toast from 'react-hot-toast';
import { Loader2, Truck, RefreshCcw, Sunrise } from 'lucide-react';

const BRIEF_TONES = {
  alert: 'border-rose-300 bg-rose-50/70 text-rose-800',
  warn:  'border-amber-300 bg-amber-50/70 text-amber-800',
  info:  'border-blue-200 bg-blue-50/60 text-blue-800',
  good:  'border-emerald-200 bg-emerald-50/60 text-emerald-800',
};

function Tile({ label, value, info, tone = 'plain', onClick }) {
  const tones = {
    plain: 'border-gray-200 bg-white',
    ok:    'border-emerald-200 bg-emerald-50/50',
    warn:  'border-amber-300 bg-amber-50/60',
    alarm: 'border-rose-300 bg-rose-50/60',
  };
  const text = {
    plain: 'text-gray-900', ok: 'text-emerald-700', warn: 'text-amber-700', alarm: 'text-rose-700',
  };
  return (
    <div className={`rounded-xl border p-4 ${tones[tone]}`}>
      <div className="flex items-center justify-between gap-1">
        <button onClick={onClick} title="See which orders these are"
                className="text-[11px] uppercase tracking-wide text-blue-600 hover:underline text-left">
          {label}
        </button>
        <InfoTip title={label}>{info}</InfoTip>
      </div>
      <button onClick={onClick} title="See which orders these are"
              className={`text-3xl font-semibold tabular-nums hover:underline ${text[tone]}`}>
        {value}
      </button>
    </div>
  );
}

export default function OpsDesk() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { drill, drillData, drillLoading, openDrill, exportDrill, closeDrill } = useDrill();

  function load() {
    setLoading(true);
    opsDeskApi.build()
      .then((r) => setData(r.data))
      .catch((err) => toast.error(err.response?.data?.error || 'Failed to load'))
      .finally(() => setLoading(false));
  }
  useEffect(() => { load(); }, []);

  const b = data?.queue.buckets;

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <Truck className="w-5 h-5 text-emerald-600" />
          <h1 className="text-2xl font-semibold text-gray-900">Ops Desk</h1>
          <InfoTip title="What this dashboard is for" align="left">
            The Ops team's desk, measured the same way the owners' cockpit
            measures it. The age tiles are the queue by urgency; the
            by-reason table says <i>why</i> orders are with Ops; the tracking
            row is the parcel chase-work; and "done this week" is the
            evidence of work completed. Every number opens the exact orders
            and exports to CSV.
          </InfoTip>
        </div>
        <button onClick={load} disabled={loading}
                className="flex items-center gap-1 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
        </button>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        The Ops queue, the parcel chase-work, and what got done — the owners see these same numbers.
      </p>

      {loading && !data && (
        <div className="py-16 text-center text-sm text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
        </div>
      )}

      {data && (
        <>
          {data.brief && (
            <div className="mb-5 rounded-xl border border-gray-200 bg-white p-4">
              <div className="flex items-center gap-1.5 mb-2">
                <Sunrise className="w-4 h-4 text-amber-500" />
                <span className="text-sm font-semibold text-gray-900">Since yesterday</span>
                <InfoTip title="Morning brief">
                  What changed in the last 24 hours, so the day starts with
                  the delta instead of re-scanning every list. The two
                  "crossed the line" rows show only parcels that became
                  chases <b>since yesterday</b> — the full backlogs are in
                  the chase-work tiles below. Click anything for the orders;
                  every list exports.
                </InfoTip>
                {data.brief.every((it) => it.severity === 'good' || it.severity === 'info' || it.count === 0) && (
                  <span className="ml-auto text-xs text-emerald-700">quiet night — nothing broke</span>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {data.brief.map((it) => (
                  <button key={it.label}
                          onClick={() => openDrill(it.key, it.params || {})}
                          title={it.detail}
                          disabled={it.count === 0}
                          className={`flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-left text-sm ${
                            it.count === 0
                              ? 'border-gray-200 bg-gray-50 text-gray-400'
                              : `${BRIEF_TONES[it.severity]} hover:ring-2 hover:ring-blue-300`
                          }`}>
                    <span className="min-w-0">
                      <span className="block truncate font-medium">{it.label}</span>
                      <span className="block text-[11px] opacity-75 truncate">{it.detail}</span>
                    </span>
                    <span className="text-xl font-semibold tabular-nums shrink-0">{it.count}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-2">
            Waiting on Ops · <strong>{data.queue.total}</strong> order(s){' '}
            <InfoTip title="Waiting on Ops">
              Orders currently owned by the Ops team, bucketed by how long
              since the last handoff. Anything past 5 days shows on the
              owners' cockpit as "stuck" — clear those first.
            </InfoTip>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
            <Tile label="< 24h" value={b.fresh_lt_24h} tone={b.fresh_lt_24h > 0 ? 'ok' : 'plain'}
                  onClick={() => openDrill('ops_queue', { bucket: 'fresh_lt_24h' })}
                  info="Fresh arrivals — normal flow." />
            <Tile label="1-2 days" value={b.aged_24_48h} tone={b.aged_24_48h > 0 ? 'warn' : 'plain'}
                  onClick={() => openDrill('ops_queue', { bucket: 'aged_24_48h' })}
                  info="Sitting a second day — scan for anything blocked." />
            <Tile label="2-7 days" value={b.aged_2_7d} tone={b.aged_2_7d > 0 ? 'warn' : 'plain'}
                  onClick={() => openDrill('ops_queue', { bucket: 'aged_2_7d' })}
                  info="Aging — past 5 days these count as stuck on the owners' cockpit." />
            <Tile label="> 7 days" value={b.aged_gt_7d} tone={b.aged_gt_7d > 0 ? 'alarm' : 'plain'}
                  onClick={() => openDrill('ops_queue', { bucket: 'aged_gt_7d' })}
                  info="Red on the owners' cockpit. Clear or escalate these today." />
          </div>

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-5">
            <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 text-sm font-semibold">
              Why orders are with Ops{' '}
              <InfoTip title="Reading this table">
                The Ops queue grouped by the reason each order landed there —
                e.g. "Partial shipment — needs customer notification" means
                the customer must be told part of their order ships
                separately. "Oldest" is the worst case per reason. Click a
                count for the orders; export to share in the team chat.
              </InfoTip>
            </div>
            {data.queue.byReason.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-gray-500">
                Nothing waiting on Ops. Clean desk.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                  <tr>
                    <th className="text-left px-3 py-2">Reason</th>
                    <th className="text-right px-3 py-2">Orders</th>
                    <th className="text-right px-3 py-2">Oldest</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.queue.byReason.map((r) => (
                    <tr key={r.reason_code} className="hover:bg-gray-50">
                      <td className="px-3 py-2">{r.reason_label || r.reason_code.replace(/_/g, ' ')}</td>
                      <td className="px-3 py-2 text-right">
                        <button onClick={() => openDrill('ops_queue', { reason: r.reason_code })}
                                title="See which orders these are"
                                className="tabular-nums text-blue-600 hover:underline font-medium">
                          {r.count}
                        </button>
                      </td>
                      <td className={`px-3 py-2 text-right text-xs tabular-nums ${r.oldest_days > 5 ? 'text-rose-600 font-semibold' : 'text-gray-500'}`}>
                        {r.oldest_days}d
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-2">
            Parcel chase-work{' '}
            <InfoTip title="Parcel chase-work">
              The tracking interventions Ops owns, same definitions as the
              Tracking Health page: parcels that stopped moving, parcels
              stuck past two weeks, and returns needing a reship-or-refund
              decision.
            </InfoTip>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
            <Tile label="Active tracking #s" value={data.tracking.activeTns}
                  onClick={() => openDrill('active_tns')}
                  info="Parcels still moving with India Post — the watch list, longest in transit first." />
            <Tile label="No movement 7+ days" value={data.tracking.noMovement7d}
                  tone={data.tracking.noMovement7d > 0 ? 'warn' : 'plain'}
                  onClick={() => openDrill('no_movement_7d')}
                  info="Tracking went quiet for over a week — flag to India Post before the customer asks." />
            <Tile label="Stuck > 14 days" value={data.tracking.stuckGt14}
                  tone={data.tracking.stuckGt14 > 0 ? 'alarm' : 'plain'}
                  onClick={() => openDrill('stuck_in_transit_gt_14d')}
                  info="In transit over two weeks — these need an India Post complaint reference. Same number the owners see." />
            <Tile label="Returned — decide" value={data.tracking.returnedNeedsAction}
                  tone={data.tracking.returnedNeedsAction > 0 ? 'alarm' : 'plain'}
                  onClick={() => openDrill('returned_needs_action')}
                  info="India Post sent the parcel back and the order is still open — each needs a reship-or-refund decision." />
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-1">
              <button onClick={() => openDrill('delivered_last_7d')} title="See which orders these are"
                      className="text-sm text-blue-600 hover:underline">
                Delivered this week: <strong>{data.done.delivered7d}</strong>
              </button>
              <InfoTip title="Delivered this week">
                Orders India Post marked delivered in the last 7 days — the
                outcome Ops is shepherding every parcel towards.
              </InfoTip>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => openDrill('ops_actions_7d')} title="See every action with order and who did it"
                      className="text-sm text-blue-600 hover:underline">
                Ops actions this week: <strong>{data.done.actions7d}</strong>
              </button>
              <InfoTip title="Ops actions this week">
                Every handoff an Ops user performed in the last 7 days —
                notifications sent, orders moved on, holds, escalations.
                The team's work-done evidence, with who did what.
              </InfoTip>
            </div>
            <span className="text-xs text-gray-400">as of {data.asOf}</span>
          </div>
        </>
      )}

      {drill && (
        <DrillModal data={drillData} loading={drillLoading}
                    onClose={closeDrill} onExport={exportDrill} />
      )}
    </div>
  );
}
